"use client";

import React from "react";
import { STARTER_ONBOARDING_STEPS } from "./steps";
import { useOnboarding } from "./OnboardingContext";
import { CheckCircle, ChevronRight, X, RotateCcw, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUserInfo, saveUserInfo } from "@/services/userService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// @ts-ignore
import { faRocket } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { getAvailableFeatures } from "@/utils/featuresUtil";

export function OnboardingPill() {
  const { statuses, canComplete, openStep, setStatus, activeStep, closeActive, loading, presence } = useOnboarding();
  const [expanded, setExpanded] = React.useState(false);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ x: number; y: number }>({ x: 16, y: 16 });
  const dragRef = React.useRef(false);
  const movedRef = React.useRef(false);
  const startRef = React.useRef({ x: 0, y: 0, px: 0, py: 0 });
  const panelSaveTimer = React.useRef<number | null>(null);
  const lastPanelSaveAt = React.useRef<number>(0);

  const total = STARTER_ONBOARDING_STEPS.length;
  const done = STARTER_ONBOARDING_STEPS.filter(s => statuses[s.key] === "done").length;
  const progress = Math.round((done / total) * 100);
  const titleByKey = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const s of STARTER_ONBOARDING_STEPS) m[s.key] = s.title;
    return m;
  }, []);

  // Build reverse dependency map: which steps depend on a given step
  const dependentsByKey = React.useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const s of STARTER_ONBOARDING_STEPS) {
      const deps = (s as any).dependsOn || [];
      for (const d of deps) {
        if (!m[d]) m[d] = [];
        m[d].push(s.key);
      }
    }
    return m;
  }, []);

  // Track viewport width for edge-aware positioning
  const [vw, setVw] = React.useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0);
  React.useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const MARGIN = 8; // viewport margin for safe area
  const PANEL_W = 360; // width of the dropdown panel
  // Header measurement ref (used for orientation and clamping)
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const [headerWidth, setHeaderWidth] = React.useState<number>(PANEL_W);
  const ICON_W = 44; // h-11 w-11
  const GAP = 8; // ml-2/mr-2
  // Measure header width when expanded to produce stable calculations across renders
  React.useLayoutEffect(() => {
    if (expanded && headerRef.current) {
      const w = headerRef.current.offsetWidth || PANEL_W;
      if (Math.abs(w - headerWidth) > 1) setHeaderWidth(w);
    }
  }, [expanded, vw, pos.x, headerWidth]);
  // Visual layout keeps header to the RIGHT of the icon always. We still clamp the group within the viewport.
  // Panel anchor offset so the panel aligns under the header (always to the right of icon)
  const panelBaseX = ICON_W + GAP;
  // Unified horizontal shift so header and panel both stay within viewport
  // Computes a single translateX to keep the group (icon+header+panel) within left/right bounds
  const groupShiftX = React.useMemo(() => {
    if (!expanded || !vw) return 0;
    const headerW = headerWidth;
    const panelLeft0 = pos.x + panelBaseX; // actual panel left relative to icon
    const panelRight0 = panelLeft0 + PANEL_W;
    const headerLeft0 = pos.x + ICON_W + GAP; // header always right of icon
    const headerRight0 = headerLeft0 + headerW;
    const iconLeft0 = pos.x;
    const iconRight0 = pos.x + ICON_W;

    const leftBound = MARGIN;
    const rightBound = vw - MARGIN;

    // Positive means we must move right; negative means we must move left
    const minLeft = Math.min(panelLeft0, headerLeft0, iconLeft0);
    const maxRight = Math.max(panelRight0, headerRight0, iconRight0);
    const lower = leftBound - minLeft;   // s must be >= lower
    const upper = rightBound - maxRight; // s must be <= upper

    // If 0 is inside [lower, upper], no shift needed
    if (lower <= 0 && upper >= 0) return 0;
    // If we need to move right and it's feasible within upper bound
    if (lower > 0 && lower <= upper) return lower;
    // If we need to move left and it's feasible within lower bound
    if (upper < 0 && upper >= lower) return upper;
    // Interval empty: choose the smaller magnitude to minimise visible overflow
    return Math.abs(lower) < Math.abs(upper) ? lower : upper;
  }, [expanded, vw, pos.x, headerWidth, panelBaseX]);

  // Load saved position/collapsed from user profile
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info: any = await getUserInfo();
        if (!mounted) return;
        const saved = info || {};
        const savedPos = saved.onboardingPillPosition as { x: number; y: number } | undefined;
        const collapsed = saved.onboardingPillCollapsed as boolean | undefined;
        const panelCollapsed = saved.onboardingPillPanelCollapsed as boolean | undefined;
        if (typeof collapsed === 'boolean') {
          setExpanded(!collapsed);
          // If we have a panel-specific persisted state, honor it; else mirror expanded
          if (typeof panelCollapsed === 'boolean') {
            setPanelOpen(!panelCollapsed);
          } else {
            setPanelOpen(!collapsed);
          }
        }

        if (savedPos) {
          setPos(savedPos);
        } else {
          // Smart default near NotificationCenter
          const computeDefault = () => {
            const margin = MARGIN;
            const iconW = ICON_W;
            const topY = margin; // align to navbar top by default
            const candidates = [
              '[data-testid*="notification"]',
              '[aria-label*="Notification"]',
              '[title*="Notification"]',
              '#notification-center',
              '.notification-center',
              '[class*="notification"]',
            ];
            let target: Element | null = null;
            for (const sel of candidates) {
              target = document.querySelector(sel);
              if (target) break;
            }
            if (!target) {
              // Fallback to right controls group in nav bar
              target = document.querySelector('.navigation-bar .ml-auto');
            }
            if (target) {
              const r = (target as HTMLElement).getBoundingClientRect();
              let x = r.left - iconW - 8;
              x = Math.max(margin, Math.min((window.innerWidth - margin - iconW), x));
              const y = Math.max(margin, Math.min(window.innerHeight - margin - iconW, Math.round(r.top + (r.height - iconW) / 2)));
              return { x, y };
            }
            // Final fallback: top-right safe area
            const x = Math.max(margin, window.innerWidth - margin - iconW);
            const y = topY;
            return { x, y };
          };
          // Wait a frame to ensure navbar is rendered
          requestAnimationFrame(() => {
            const d = computeDefault();
            if (!mounted) return;
            setPos(d);
          });
        }
      } catch (_) {
        // ignore
      }
    })();
    return () => { mounted = false };
  }, []);

  // Cleanup any pending debounce on unmount
  React.useEffect(() => {
    return () => {
      if (panelSaveTimer.current) {
        clearTimeout(panelSaveTimer.current);
        panelSaveTimer.current = null;
      }
    };
  }, []);

  // Debounced/throttled saver for panel-only collapsed state
  const queuePanelSave = React.useCallback((panelCollapsed: boolean) => {
    const now = Date.now();
    const elapsed = now - lastPanelSaveAt.current;
    const QUIET_WINDOW = 500; // ms
    const TRAILING_DELAY = 150; // ms
    if (panelSaveTimer.current) {
      clearTimeout(panelSaveTimer.current);
      panelSaveTimer.current = null;
    }
    if (elapsed > QUIET_WINDOW) {
      // Save immediately when it's been quiet
      saveUserInfo({ onboardingPillPanelCollapsed: panelCollapsed }).catch(() => {});
      lastPanelSaveAt.current = Date.now();
    } else {
      // Otherwise, schedule a short trailing save
      panelSaveTimer.current = window.setTimeout(() => {
        saveUserInfo({ onboardingPillPanelCollapsed: panelCollapsed }).catch(() => {});
        lastPanelSaveAt.current = Date.now();
      }, TRAILING_DELAY);
    }
  }, []);

  const saveCollapsed = (collapsed: boolean) => {
    saveUserInfo({ onboardingPillCollapsed: collapsed }).catch(() => {});
  };

  const savePosition = (p: { x: number; y: number }) => {
    saveUserInfo({ onboardingPillPosition: p }).catch(() => {});
  };

  // Drag handlers (header area only)
  const onPointerDown = (e: React.PointerEvent) => {
    dragRef.current = true;
    movedRef.current = false;
    startRef.current = { x: pos.x, y: pos.y, px: e.clientX, py: e.clientY };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - startRef.current.px;
    const dy = e.clientY - startRef.current.py;
    if (Math.abs(dx) + Math.abs(dy) > 3) movedRef.current = true;
    let nx = startRef.current.x + dx;
    let ny = startRef.current.y + dy;
    if (typeof window !== 'undefined') {
      nx = Math.max(MARGIN, Math.min(window.innerWidth - MARGIN - ICON_W, nx));
      ny = Math.max(MARGIN, Math.min(window.innerHeight - MARGIN - ICON_W, ny));
    }
    setPos({ x: nx, y: ny });
  };
  const onPointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = false;
    if (movedRef.current) savePosition(pos);
  };

  // Progress ring values
  const R = 18;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - progress / 100);

  const handleIconClick = () => {
    if (dragRef.current || movedRef.current) return;
    if (expanded) {
      setExpanded(false);
      setPanelOpen(false);
      saveCollapsed(true);
      queuePanelSave(true);
    } else {
      setExpanded(true);
      setPanelOpen(true);
      saveCollapsed(false);
      queuePanelSave(false);
    }
  };

  const handleHeaderClick = () => {
    if (dragRef.current || movedRef.current) return;
    setPanelOpen(prev => {
      const next = !prev;
      // Persist panel-only collapsed state
      queuePanelSave(!next);
      return next;
    });
  };

  return null;

  // return (
  //   <div className="fixed z-60" style={{ top: pos.y, left: pos.x + ((expanded && vw) ? groupShiftX : 0) }}>
  //     {/* Header/Handle: icon + (optional) bar */}
  //     <div
  //       className={cn("flex items-center select-none cursor-grab active:cursor-grabbing", "flex-row")}
  //       onPointerDown={onPointerDown}
  //       onPointerMove={onPointerMove}
  //       onPointerUp={onPointerUp}
  //     >
  //       {/* Compact icon with progress ring */}
  //       <button
  //         type="button"
  //         onClick={handleIconClick}
  //         className="relative h-11 w-11 aspect-square rounded-full bg-card/70 supports-backdrop-filter:bg-card/60 backdrop-blur-xl border border-white/10 shadow-2xl grid place-items-center flex-none shrink-0 leading-none"
  //         aria-label="Starter Onboarding"
  //         aria-expanded={expanded}
  //       >
  //         <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 48 48">
  //           <circle cx="24" cy="24" r={R} stroke="currentColor" className="text-white/15" strokeWidth="3" fill="none" />
  //           <circle
  //             cx="24" cy="24" r={R}
  //             stroke="currentColor"
  //             className="text-primary"
  //             strokeWidth="3"
  //             fill="none"
  //             strokeDasharray={C}
  //             strokeDashoffset={offset}
  //             strokeLinecap="round"
  //           />
  //         </svg>
  //         <FontAwesomeIcon icon={faRocket} className="h-4 w-4 text-primary" />
  //       </button>

  //       {expanded && (
  //         <div ref={headerRef} className={cn("rounded-full bg-card/70 supports-backdrop-filter:bg-card/60 backdrop-blur-xl border border-white/10 shadow-2xl inline-flex w-[360px]", "ml-2") }>
  //           <button
  //             className="flex items-center gap-2 h-11 px-4 text-sm w-full justify-between whitespace-nowrap"
  //             onClick={handleHeaderClick}
  //             aria-expanded={panelOpen}
  //             aria-controls="onboarding-panel"
  //           >
  //             <div className="text-left">
  //               <div className="font-medium">Starter Onboarding</div>
  //               <div className="text-xs text-muted-foreground">{done}/{total} • {progress}%</div>
  //             </div>
  //             <ChevronRight className={cn("h-4 w-4 transition-transform ml-1", panelOpen ? "rotate-90" : "rotate-0")} />
  //           </button>
  //         </div>
  //       )}
  //     </div>

  //     {/* Panel */}
  //     {expanded && panelOpen && (
  //       <div id="onboarding-panel" className="mt-2 w-[360px] rounded-xl bg-card/80 supports-backdrop-filter:bg-card/60 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden" style={{ transform: `translateX(${panelBaseX}px)` }}>
  //         <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
  //           <div className="text-sm font-medium">Pick a step</div>
  //           {activeStep && (
  //             <button onClick={closeActive} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
  //               <X className="h-3.5 w-3.5" /> Close modal
  //             </button>
  //           )}
  //         </div>
  //         <div className="max-h-[60vh] overflow-y-auto">
  //           {loading ? (
  //             <div className="px-4 py-8 text-center text-sm text-muted-foreground">
  //               Loading onboarding progress...
  //             </div>
  //           ) : (
  //             STARTER_ONBOARDING_STEPS.map(step => {
  //               const status = statuses[step.key];
  //               const depsOk = canComplete(step.key);
  //               const hasDependents = ((dependentsByKey[step.key] || []).length > 0);
  //               const activeUsers = presence[step.key] || [];
  //               return (
  //                 <div key={step.key} className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
  //                   <div className={cn("h-2 w-2 rounded-full self-center",
  //                     status === "done" ? "bg-green-500" : status === "in_progress" ? "bg-blue-500" : "bg-white/30"
  //                   )} />
  //                   <div className="flex-1 min-w-0">
  //                     <div className="text-sm font-medium truncate flex items-center gap-2">
  //                       {step.title}
  //                       {/* Live presence indicator */}
  //                       {activeUsers.length > 0 && (
  //                         <span 
  //                           className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-medium animate-pulse"
  //                           title={activeUsers.map(u => u.userName).join(', ')}
  //                         >
  //                           <span className="relative flex h-1.5 w-1.5">
  //                             <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
  //                             <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
  //                           </span>
  //                           {activeUsers.length === 1 
  //                             ? activeUsers[0].userName.split(' ')[0]
  //                             : `${activeUsers.length} users`
  //                           }
  //                         </span>
  //                       )}
  //                     </div>
  //                     {step.description && <div className="text-xs text-muted-foreground truncate">{step.description}</div>}
  //                     {step.dependsOn && step.dependsOn.length > 0 && (
  //                       <div className="text-[11px] text-muted-foreground mt-1">
  //                         Depends on: {(step.dependsOn || []).map(k => titleByKey[k] ?? k).join(", ")}
  //                       </div>
  //                     )}
  //                   </div>
  //                   <div className="flex items-center gap-2">
  //                     {status !== "done" ? (
  //                       <button
  //                         onClick={() => openStep(step.key)}
  //                         disabled={!depsOk}
  //                         className={cn("text-xs px-2.5 py-1 rounded-md border",
  //                           depsOk ? "border-primary/30 text-primary hover:bg-primary/10" : "border-white/10 text-muted-foreground cursor-not-allowed")}
  //                       >
  //                         {status === "in_progress" ? "Resume" : "Start"}
  //                       </button>
  //                     ) : (
  //                       <>
  //                         <div className="text-green-600 text-xs flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" /> Done</div>
  //                         <button
  //                           onClick={() => {
  //                             setStatus(step.key, "in_progress");
  //                             openStep(step.key);
  //                           }}
  //                           className="text-xs px-2.5 py-1 rounded-md border border-amber-500/30 text-amber-600 hover:bg-amber-500/10 flex items-center gap-1"
  //                           title="Redo this step"
  //                         >
  //                           <RotateCcw className="h-3 w-3" /> Redo
  //                         </button>
  //                       </>
  //                     )}
  //                     {/* Show "Mark done" for steps that have dependencies (can be skipped) 
  //                         but NOT for steps that are depended upon by others (must complete) */}
  //                     {status !== "done" && depsOk && (step.dependsOn && step.dependsOn.length > 0) && (
  //                       <button
  //                         onClick={() => setStatus(step.key, "done")}
  //                         className="text-xs px-2.5 py-1 rounded-md border border-green-500/30 text-green-600 hover:bg-green-500/10"
  //                       >
  //                         Mark done
  //                       </button>
  //                     )}
  //                   </div>
  //                 </div>
  //               );
  //             })
  //           )}
  //         </div>
  //       </div>
  //     )}
  //   </div>
  // );
}
